import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const AnimatedNumber = ({ value, duration = 2 }: { value: number; duration?: number }) => {
  const nodeRef = useRef(null);
  const inView = useInView(nodeRef, { once: true, margin: "-100px" });
  const [displayValue, setDisplayValue] = React.useState(0);

  React.useEffect(() => {
    if (inView) {
      let startTime: number;
      let animationFrame: number;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = (timestamp - startTime) / (duration * 1000);

        if (progress < 1) {
          setDisplayValue(Math.floor(value * progress));
          animationFrame = requestAnimationFrame(animate);
        } else {
          setDisplayValue(value);
        }
      };

      animationFrame = requestAnimationFrame(animate);

      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }
  }, [inView, value, duration]);

  return <span ref={nodeRef}>{displayValue.toLocaleString()}</span>;
};

export const StatsSection = () => {
  const { t } = useTranslation();

  const stats = [
    { value: 50000, key: 'contents' },
    { value: 30, key: 'algorithms' },
    { value: 10000, key: 'products' },
    { value: 1000, key: 'revenue' },
  ];

  return (
    <section className="section py-12">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="grid grid-cols-1 gap-y-12 sm:gap-y-16 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-8">
          {stats.map(({ value, key }) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center group"
            >
              <h3 className="text-5xl sm:text-6xl font-bold mb-3 bg-clip-text text-transparent premium-gradient">
                {key === 'revenue' ? (
                  <>
                    +<AnimatedNumber value={value} />
                  </>
                ) : key === 'algorithms' ? (
                  <>
                    +<AnimatedNumber value={value} />
                  </>
                ) : (
                  <>
                    +<AnimatedNumber value={value} />
                  </>
                )}
              </h3>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 font-medium relative inline-block">
                {t(`stats.${key}`)}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[var(--gradient-start)] via-[var(--gradient-middle)] to-[var(--gradient-end)] group-hover:w-full transition-all duration-300" />
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};